/**
 * WHICH BUILD A DOCUMENT IS.
 *
 * Vite names every built file under `/assets/` with a content hash. `index.html`
 * is the list of those names for one release, and it is the file a reload
 * fetches. Two documents are the same release when that list is the same.
 *
 * Dev has no hashed name (`/src/main.tsx`, `/@vite/client`), so it has no
 * release to compare and the reminder stays quiet. A classic script such as
 * `/zod-jitless.js` is unhashed on purpose and is not part of the list.
 */

const TAG = /<(script|link)\b([^>]*)>/gi;
const ATTR = /([^\s=/>]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;

function attributes(source: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const match of source.matchAll(new RegExp(ATTR, "g"))) {
    const name = match[1]?.toLowerCase();
    if (name === undefined) continue;
    map.set(name, match[2] ?? match[3] ?? "");
  }
  return map;
}

/**
 * The pathname of a content-hashed build asset, or null.
 *
 * Root-relative, as the build writes it, or absolute, as a host might rewrite
 * it. Query and hash are dropped. Nested paths are not assets Vite emits.
 */
export function hashedAssetPath(value: string): string | null {
  const raw = value.trim();
  if (raw === "") return null;
  let path = raw;
  if (raw.startsWith("https://") || raw.startsWith("http://")) {
    try {
      path = new URL(raw).pathname;
    } catch {
      return null;
    }
  }
  const cut = path.split(/[?#]/)[0] ?? "";
  return /^\/assets\/[^/]+$/.test(cut) ? cut : null;
}

/** The release id of an HTML document, or null when it names no hashed asset. */
export function shellRelease(html: string): string | null {
  const refs: string[] = [];
  for (const match of html.matchAll(new RegExp(TAG, "gi"))) {
    const name = match[1]?.toLowerCase();
    const attr = attributes(match[2] ?? "");
    if (name === "script" && attr.get("type")?.toLowerCase() === "module") {
      const src = attr.get("src");
      if (src !== undefined) refs.push(src);
    } else if (name === "link") {
      const rel = attr.get("rel")?.toLowerCase().split(/\s+/) ?? [];
      if (rel.includes("stylesheet") || rel.includes("modulepreload")) {
        const href = attr.get("href");
        if (href !== undefined) refs.push(href);
      }
    }
  }
  return shellReleaseFromRefs(refs);
}

/**
 * One id for a set of URLs. Order and duplicates do not matter. Null when
 * none of them is a hashed asset — that is dev, or a document we could not read.
 */
export function shellReleaseFromRefs(refs: readonly string[]): string | null {
  const hashed = new Set<string>();
  for (const ref of refs) {
    const path = hashedAssetPath(ref);
    if (path !== null) hashed.add(path);
  }
  if (hashed.size === 0) return null;
  return [...hashed].sort().join("\n");
}
